import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Button } from "@toddle-edu/ds-web";

import { useI18n } from "Hooks";
import classes from "./CohortsList.scss";
import Loading from "UIComponents/Loading";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import ProfileCascade from "UIComponents/ProfileCascade";
import { STAFF, STUDENT, PARENT } from "Constants/stringConstants";

const avatarStyles = {
  size: "medium",
  shade: "light",
  randomizeColor: true,
  shape: "square",
};

const CohortSelectionCard = props => {
  const { userList = [], count, title, id, onClick, type, isGrade } = props;

  const { t } = useI18n(["common"]);

  const userCount = count || _.size(userList);

  const mapSubtitleTextToUserType = {
    [STUDENT]: t("common:student_count", { count: userCount }),
    [STAFF]: t("common:staff_count_lowercase", { count: userCount }),
    [PARENT]: t("common:family_member_count", { count: userCount }),
  };

  const userSubTitleText = mapSubtitleTextToUserType[type];

  const handleCohortClick = () =>
    isGrade ? onClick({ gradeId: id, userType: type }) : onClick(userList);

  const handleCohortKeyDown = e => onEnterOrSpaceKeyDown(e, handleCohortClick);

  const userSubTitleClasses = classNames(classes.userSubTitle, "text-body-2");
  return (
    <div
      className={classes.selectionCardContainer}
      onClick={handleCohortClick}
      onKeyDown={handleCohortKeyDown}
      role="button"
      tabIndex={0}
      aria-label={title}
    >
      <div className={classes.userAvatar}>
        <ProfileCascade
          items={[
            {
              id,
              value: title,
            },
          ]}
          shouldDynamicColor
          showAvatar
          avatarStyles={avatarStyles}
        />
      </div>
      <div className={classes.userInfoContainer}>
        <div className="text-body-1">{title}</div>
        <div className={userSubTitleClasses}>{userSubTitleText}</div>
      </div>
    </div>
  );
};

const UserTypeList = props => {
  const {
    headerTitle,
    onClick,
    cohorts,
    isFetchingMore,
    hasNextPage,
    loadMoreUsers,
    isGrade,
    userTypeListContainerClassName,
  } = props;

  const { t } = useI18n(["common"]);
  const headerContainer = classNames(classes.headerContainer, "text-label-2");

  const containerClasses = classNames(
    classes.container,
    userTypeListContainerClassName
  );

  return (
    <div className={containerClasses}>
      <div className={headerContainer}>{headerTitle}</div>
      {_.map(cohorts, cohort => {
        const { userList, id, title, type, count } = cohort;
        return (
          <CohortSelectionCard
            key={`${id}-${type}`}
            id={id}
            type={type}
            title={title}
            onClick={onClick}
            isGrade={isGrade}
            count={count}
            userList={userList}
          />
        );
      })}

      {isFetchingMore ? (
        <Loading />
      ) : hasNextPage ? (
        <div className={classes.footerContainer}>
          <Button onClick={loadMoreUsers} variant="inline-progressive">
            <div className="text-label-3">{t("common:show_more")}</div>
          </Button>
        </div>
      ) : null}
    </div>
  );
};

UserTypeList.propTypes = {
  cohorts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      userList: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ),
  onClick: PropTypes.func.isRequired,
  headerTitle: PropTypes.string.isRequired,
  isFetchingMore: PropTypes.bool.isRequired,
  hasNextPage: PropTypes.bool.isRequired,
  loadMoreUsers: PropTypes.func.isRequired,
};

export default UserTypeList;
